import React, { useRef, forwardRef, useState, useEffect } from 'react';
import { Modal, Form, InputGroup, Button } from 'react-bootstrap';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa';
import { TiWarning } from 'react-icons/ti';
import BootstrapTable from 'react-bootstrap-table-next';
import cellEditFactory, { Type } from 'react-bootstrap-table2-editor';
import moment from 'moment';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import { BiCalendar } from 'react-icons/bi';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'menos de un segundo',
    other: 'menos de {{count}} segundos'
  },
  xSeconds: {
    one: '1 segundo',
    other: '{{count}} segundos'
  },
  halfAMinute: 'medio minuto',
  lessThanXMinutes: {
    one: 'menos de un minuto',
    other: 'menos de {{count}} minutos'
  },
  xMinutes: {
    one: '1 minuto',
    other: '{{count}} minutos'
  },
  aboutXHours: {
    one: 'alrededor de 1 hora',
    other: 'alrededor de {{count}} horas'
  },
  xHours: {
    one: '1 hora',
    other: '{{count}} horas'
  },
  xDays: {
    one: '1 día',
    other: '{{count}} días'
  },
  aboutXWeeks: {
    one: 'alrededor de 1 semana',
    other: 'alrededor de {{count}} semanas'
  },
  xWeeks: {
    one: '1 semana',
    other: '{{count}} semanas'
  },
  aboutXMonths: {
    one: 'alrededor de 1 mes',
    other: 'alrededor de {{count}} meses'
  },
  xMonths: {
    one: '1 mes',
    other: '{{count}} meses'
  },
  aboutXYears: {
    one: 'alrededor de 1 año',
    other: 'alrededor de {{count}} años'
  },
  xYears: {
    one: '1 año',
    other: '{{count}} años'
  },
  overXYears: {
    one: 'más de 1 año',
    other: 'más de {{count}} años'
  },
  almostXYears: {
    one: 'casi 1 año',
    other: 'casi {{count}} años'
  }
};

var formatDistance = function (token, count, options) {
  var result;
  var tokenValue = formatDistanceLocale[token];

  if (typeof tokenValue === 'string') {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace('{{count}}', count.toString());
  }

  if (options !== null && options !== void 0 && options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return 'en ' + result;
    } else {
      return 'hace ' + result;
    }
  }

  return result;
};

function buildFormatLongFn(args) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO: Remove String()
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

var dateFormats = {
  full: "EEEE, d 'de' MMMM 'de' y",
  long: "d 'de' MMMM 'de' y",
  medium: 'd MMM y',
  short: 'dd/MM/y'
};
var timeFormats = {
  full: 'HH:mm:ss zzzz',
  long: 'HH:mm:ss z',
  medium: 'HH:mm:ss',
  short: 'HH:mm'
};
var dateTimeFormats = {
  full: "{{date}} 'a las' {{time}}",
  long: "{{date}} 'a las' {{time}}",
  medium: '{{date}}, {{time}}',
  short: '{{date}}, {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};

var formatRelativeLocale = {
  lastWeek: "'el' eeee 'pasado a la' p",
  yesterday: "'ayer a la' p",
  today: "'hoy a la' p",
  tomorrow: "'mañana a la' p",
  nextWeek: "eeee 'a la' p",
  other: 'P'
};
var formatRelativeLocalePlural = {
  lastWeek: "'el' eeee 'pasado a las' p",
  yesterday: "'ayer a las' p",
  today: "'hoy a las' p",
  tomorrow: "'mañana a las' p",
  nextWeek: "eeee 'a las' p",
  other: 'P'
};

var formatRelative = function (token, date, _baseDate, _options) {
  if (date.getUTCHours() !== 1) {
    return formatRelativeLocalePlural[token];
  } else {
    return formatRelativeLocale[token];
  }
};

function buildLocalizeFn(args) {
  return function (dirtyIndex, dirtyOptions) {
    var options = dirtyOptions || {};
    var context = options.context ? String(options.context) : 'standalone';
    var valuesArray;

    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;

      var _width = options.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }

    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex; // @ts-ignore: For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!

    return valuesArray[index];
  };
}

var eraValues = {
  narrow: ['AC', 'DC'],
  abbreviated: ['AC', 'DC'],
  wide: ['antes de cristo', 'después de cristo']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['T1', 'T2', 'T3', 'T4'],
  wide: ['1º trimestre', '2º trimestre', '3º trimestre', '4º trimestre']
};
var monthValues = {
  narrow: ['e', 'f', 'm', 'a', 'm', 'j', 'j', 'a', 's', 'o', 'n', 'd'],
  abbreviated: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  wide: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
};
var dayValues = {
  narrow: ['d', 'l', 'm', 'm', 'j', 'v', 's'],
  short: ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'],
  abbreviated: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  wide: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
};
var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mn',
    noon: 'md',
    morning: 'mañana',
    afternoon: 'tarde',
    evening: 'tarde',
    night: 'noche'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'medianoche',
    noon: 'mediodia',
    morning: 'mañana',
    afternoon: 'tarde',
    evening: 'tarde',
    night: 'noche'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'medianoche',
    noon: 'mediodia',
    morning: 'mañana',
    afternoon: 'tarde',
    evening: 'tarde',
    night: 'noche'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mn',
    noon: 'md',
    morning: 'de la mañana',
    afternoon: 'de la tarde',
    evening: 'de la tarde',
    night: 'de la noche'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'medianoche',
    noon: 'mediodia',
    morning: 'de la mañana',
    afternoon: 'de la tarde',
    evening: 'de la tarde',
    night: 'de la noche'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'medianoche',
    noon: 'mediodia',
    morning: 'de la mañana',
    afternoon: 'de la tarde',
    evening: 'de la tarde',
    night: 'de la noche'
  }
};

var ordinalNumber = function (dirtyNumber, _options) {
  var number = Number(dirtyNumber);
  return number + 'º';
};

var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function (quarter) {
      return Number(quarter) - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};

function buildMatchPatternFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

function buildMatchFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }

    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    }) : findKey(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    });
    var value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }

  return undefined;
}

function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }

  return undefined;
}

var matchOrdinalNumberPattern = /^(\d+)(º)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(ac|dc|a|d)/i,
  abbreviated: /^(a\.?\s?c\.?|a\.?\s?e\.?\s?c\.?|d\.?\s?c\.?|e\.?\s?c\.?)/i,
  wide: /^(antes de cristo|antes de la era com[uú]n|despu[eé]s de cristo|era com[uú]n)/i
};
var parseEraPatterns = {
  any: [/^ac/i, /^dc/i],
  wide: [/^(antes de cristo|antes de la era com[uú]n)/i, /^(despu[eé]s de cristo|era com[uú]n)/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^T[1234]/i,
  wide: /^[1234](º)? trimestre/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[efmajsond]/i,
  abbreviated: /^(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)/i,
  wide: /^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)/i
};
var parseMonthPatterns = {
  narrow: [/^e/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^en/i, /^feb/i, /^mar/i, /^abr/i, /^may/i, /^jun/i, /^jul/i, /^ago/i, /^sep/i, /^oct/i, /^nov/i, /^dic/i]
};
var matchDayPatterns = {
  narrow: /^[dlmjvs]/i,
  short: /^(do|lu|ma|mi|ju|vi|s[áa])/i,
  abbreviated: /^(dom|lun|mar|mi[ée]|jue|vie|s[áa]b)/i,
  wide: /^(domingo|lunes|martes|mi[ée]rcoles|jueves|viernes|s[áa]bado)/i
};
var parseDayPatterns = {
  narrow: [/^d/i, /^l/i, /^m/i, /^m/i, /^j/i, /^v/i, /^s/i],
  any: [/^do/i, /^lu/i, /^ma/i, /^mi/i, /^ju/i, /^vi/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(a|p|mn|md|(de la|a las) (mañana|tarde|noche))/i,
  any: /^([ap]\.?\s?m\.?|medianoche|mediodia|(de la|a las) (mañana|tarde|noche))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mn/i,
    noon: /^md/i,
    morning: /mañana/i,
    afternoon: /tarde/i,
    evening: /tarde/i,
    night: /noche/i
  }
};
var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function (value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function (index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};

/**
 * @type {Locale}
 * @category Locales
 * @summary Spanish locale.
 * @language Spanish
 * @iso-639-2 spa
 * @author Juan Angosto [@juanangosto]{@link https://github.com/juanangosto}
 * @author Guillermo Grau [@guigrpa]{@link https://github.com/guigrpa}
 * @author Fernando Agüero [@fjaguero]{@link https://github.com/fjaguero}
 * @author Gastón Haro [@harogaston]{@link https://github.com/harogaston}
 * @author Yago Carballo [@YagoCarballo]{@link https://github.com/YagoCarballo}
 */
var locale = {
  code: 'es',
  formatDistance: formatDistance,
  formatLong: formatLong,
  formatRelative: formatRelative,
  localize: localize,
  match: match,
  options: {
    weekStartsOn: 1
    /* Monday */
    ,
    firstWeekContainsDate: 1
  }
};

registerLocale('es', locale);
var DatePickerComponent = function DatePickerComponent(_ref) {
  var props = _ref.props,
    name = _ref.name,
    value = _ref.value,
    _onChange = _ref.onChange,
    classNameInput = _ref.classNameInput,
    disabled = _ref.disabled,
    wrapperClassName = _ref.wrapperClassName;
  var ref = useRef(null);
  var CustomInput = forwardRef(function (_ref2, ref) {
    var value = _ref2.value,
      onClick = _ref2.onClick,
      onChange = _ref2.onChange;
    return /*#__PURE__*/React.createElement("div", {
      className: "input-group date",
      ref: ref
    }, /*#__PURE__*/React.createElement("input", {
      className: "form-control " + classNameInput,
      onClick: onClick,
      value: value,
      onChange: onChange
    }), /*#__PURE__*/React.createElement("div", {
      className: "input-group-append"
    }, /*#__PURE__*/React.createElement("span", {
      className: "input-group-text",
      onClick: onClick
    }, /*#__PURE__*/React.createElement(BiCalendar, {
      style: {
        width: '1em',
        height: '1.5em'
      }
    }))));
  });
  return /*#__PURE__*/React.createElement(DatePicker, _extends({
    dateFormat: "dd/MM/yyyy",
    locale: "es"
  }, props, {
    dropdownMode: "select",
    selected: value,
    disabled: disabled,
    className: "form-control",
    wrapperClassName: wrapperClassName,
    onChange: function onChange(date) {
      return _onChange({
        target: {
          name: name,
          value: date
        }
      });
    }
  }));
};

toastr.options = {
  positionClass: 'toast-top-right',
  showDuration: 1000,
  hideDuration: 1000,
  timeOut: 4000
};
var Notification = toastr;

var GetHeaderRequest = function GetHeaderRequest() {
  return {
    'Content-Type': 'application/json'
  };
};
var GetResponseBody = function GetResponseBody(pResponse) {
  var contentType = pResponse.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return pResponse.json();
  } else {
    return {
      Exitoso: false,
      Mensaje: 'Ocurrió un error realizando la acción.',
      CodigoError: 999
    };
  }
};
var Consultar_Lote_Request = function Consultar_Lote_Request(pUrl, pToken, pEmpresa_Id, pSucursal_Id, pBodega_Id, pArticulo_Id, pLote_Numero) {
  try {
    var data = {
      Token: pToken,
      Empresa_Id: pEmpresa_Id,
      Sucursal_Id: pSucursal_Id,
      Bodega_Id: pBodega_Id,
      Articulo_Id: pArticulo_Id,
      Lote_Numero: pLote_Numero
    };
    var url = pUrl.endsWith('/') ? pUrl : pUrl + "/";
    return Promise.resolve(fetch(url + "API/Lote/Consultar_Lote", {
      method: 'POST',
      headers: GetHeaderRequest(),
      body: JSON.stringify(data)
    })).then(function (result) {
      return Promise.resolve(GetResponseBody(result));
    });
  } catch (e) {
    return Promise.reject(e);
  }
};
var Filtrar_Lotes_Existentes_Request = function Filtrar_Lotes_Existentes_Request(pUrl, pToken, pEmpresa_Id, pArticulo_Id, pLotes) {
  try {
    var data = {
      Token: pToken,
      Empresa_Id: pEmpresa_Id,
      Articulo_Id: pArticulo_Id,
      Lotes: pLotes
    };
    var url = pUrl.endsWith('/') ? pUrl : pUrl + "/";
    return Promise.resolve(fetch(url + "API/Lote/Filtrar_Lotes_Existentes", {
      method: 'POST',
      headers: GetHeaderRequest(),
      body: JSON.stringify(data)
    })).then(function (result) {
      return Promise.resolve(GetResponseBody(result));
    });
  } catch (e) {
    return Promise.reject(e);
  }
};
var Validar_Formato_Codigo_DataMatrix_Request = function Validar_Formato_Codigo_DataMatrix_Request(pUrl, pToken, pEmpresa_Id, pCodigo_DM) {
  try {
    var data = {
      Token: pToken,
      Empresa_Id: pEmpresa_Id,
      Codigo_DM: pCodigo_DM
    };
    var url = pUrl.endsWith('/') ? pUrl : pUrl + "/";
    return Promise.resolve(fetch(url + "API/Lote/Validar_Formato_Codigo_DataMatrix", {
      method: 'POST',
      headers: GetHeaderRequest(),
      body: JSON.stringify(data)
    })).then(function (result) {
      return Promise.resolve(GetResponseBody(result));
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

var styles = {"input-control":"_35QdB","block":"_OkeoQ","header-min-width":"_2teJv"};

var obtener_fecha_sin_tiempo = function obtener_fecha_sin_tiempo(pDate) {
  pDate.setHours(0, 0, 0, 0);
  return pDate;
};
var LotesComponent = function LotesComponent(_ref) {
  var _ref$show = _ref.show,
    show = _ref$show === void 0 ? false : _ref$show,
    _ref$conexion = _ref.conexion,
    conexion = _ref$conexion === void 0 ? {
      token: '',
      serviceUrl: ''
    } : _ref$conexion,
    _ref$parametros = _ref.parametros,
    parametros = _ref$parametros === void 0 ? {
      empresa_id: 0,
      sucursal_id: 0,
      bodega_id: 0,
      modulo_id: 0,
      menu_id: 0,
      tipo_accion: 'entrada',
      cantidad_requerida: 0,
      cantidad_solicitada: 0,
      cantidad_decimales: 0,
      modo_consulta: false
    } : _ref$parametros,
    _ref$style = _ref.style,
    style = _ref$style === void 0 ? {
      color_primario: '#2D6E91'
    } : _ref$style,
    _ref$articulo = _ref.articulo,
    articulo = _ref$articulo === void 0 ? {
      Articulo_Id: '',
      Codigo_Principal: '',
      Nombre: '',
      Articulo_Dias_Vence: 0,
      Articulo_Vence: false,
      Decimales: true,
      Codigo_Padre: 0
    } : _ref$articulo,
    _ref$lstLotes = _ref.lstLotes,
    lstLotes = _ref$lstLotes === void 0 ? [] : _ref$lstLotes,
    _ref$lstEnviados = _ref.lstEnviados,
    lstEnviados = _ref$lstEnviados === void 0 ? [] : _ref$lstEnviados,
    _ref$onClose = _ref.onClose,
    onClose = _ref$onClose === void 0 ? function (lstLotes, isSaved) {
    } : _ref$onClose;
  var _useState = useState(show || false),
    modalShow = _useState[0],
    setModalShow = _useState[1];
  var _useState2 = useState(''),
    codigoDM = _useState2[0],
    setCodigoDM = _useState2[1];
  var _useState3 = useState({
      Lote: '',
      Vence: false,
      Fecha_Vencimiento: obtener_fecha_sin_tiempo(new Date()),
      Cantidad: 0
    }),
    dataMatrix = _useState3[0],
    setDataMatrix = _useState3[1];
  var _useState4 = useState([]),
    listaLotes = _useState4[0],
    setListaLotes = _useState4[1];
  var _useState5 = useState([]),
    selected = _useState5[0],
    setSelected = _useState5[1];
  var _useState6 = useState([]),
    nonSelectable = _useState6[0],
    setNonSelectable = _useState6[1];
  var _useState7 = useState(true),
    debeSumar = _useState7[0],
    setDebeSumar = _useState7[1];
  var _useState8 = useState(true),
    enabledFechaVence = _useState8[0],
    setEnabledFechaVence = _useState8[1];
  var _useState9 = useState(false),
    hayCambios = _useState9[0],
    setHayCambios = _useState9[1];
  var inputCantidadRef = useRef(null);
  var inputCodigoDMRef = useRef(null);
  var color_primario = style && style.color_primario ? style.color_primario : '#2D6E91';
  useEffect(function () {
    setModalShow(show);
    var lstExistentes = [];
    var noSeleccionable = [];
    if (parametros.tipo_accion === 'recepcion_interna' && lstEnviados) {
      lstEnviados = lstEnviados.length > 0 ? lstEnviados : [];
      lstExistentes = lstLotes.map(function (lote) {
        var enviado_filtrado = lstEnviados.filter(function (item) {
          return item.Articulo_Id === articulo.Articulo_Id && item.Lote.toUpperCase() === lote.Lote.toUpperCase();
        });
        if (enviado_filtrado && enviado_filtrado.length > 0) {
          lote.Enviado = enviado_filtrado[0].Enviado;
          noSeleccionable.push(lote.Lote);
        } else {
          lote.Enviado = 0;
        }
        lote.Diferencia = (parseFloat(lote.Enviado) - parseFloat(lote.Cantidad)).toFixed(parametros.cantidad_decimales || 0);
        return lote;
      });
      var enviados_faltantes = lstEnviados.filter(function (item) {
        return lstLotes.findIndex(function (lote) {
          return lote.Lote === item.Lote;
        }) < 0;
      });
      if (enviados_faltantes && enviados_faltantes.length > 0) {
        enviados_faltantes.forEach(function (faltante) {
          noSeleccionable.push(faltante.Lote);
          lstExistentes.push(_extends({}, faltante, {
            Cantidad: 0,
            Diferencia: faltante.Enviado
          }));
        });
      }
      establecer_lote_existe(show ? JSON.parse(JSON.stringify(lstExistentes)) : []);
    } else {
      establecer_lote_existe(show ? JSON.parse(JSON.stringify(lstLotes)) : []);
    }
    setNonSelectable(noSeleccionable);
    reset_states();
  }, [show]);
  var obtener_fecha_vencimiento_lote = function obtener_fecha_vencimiento_lote(pLote) {
    try {
      return Promise.resolve(Consultar_Lote_Request(conexion.serviceUrl, conexion.token, parametros.empresa_id, parametros.sucursal_id, parametros.bodega_id, articulo.Articulo_Id, pLote)).then(function (respuesta) {
        if (respuesta.Exitoso) {
          setDataMatrix(_extends({}, dataMatrix, {
            Fecha_Vencimiento: obtener_fecha_sin_tiempo(new Date(respuesta.Datos.Fecha_Vencimiento))
          }));
          setEnabledFechaVence(false);
          inputCantidadRef.current.focus();
        } else {
          setDataMatrix(_extends({}, dataMatrix, {
            Fecha_Vencimiento: obtener_fecha_sin_tiempo(new Date())
          }));
          setEnabledFechaVence(true);
        }
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };
  var consultar_lote = function consultar_lote(pDataMatrix, pDone, pNueva_Fecha_Vencimiento) {
    try {
      return Promise.resolve(Consultar_Lote_Request(conexion.serviceUrl, conexion.token, parametros.empresa_id, parametros.sucursal_id, parametros.bodega_id, articulo.Articulo_Id, pDataMatrix.Lote)).then(function (respuesta) {
        if (respuesta.Exitoso || parametros.tipo_accion !== 'salida') {
          if (respuesta.Exitoso && pNueva_Fecha_Vencimiento && !moment(pNueva_Fecha_Vencimiento).isSame(respuesta.Datos.Fecha_Vencimiento, 'day')) {
            Notification.error("El lote " + pDataMatrix.Lote + " se registro anteriormente con fecha de vencimiento " + moment(respuesta.Datos.Fecha_Vencimiento).format('DD/MM/YYYY') + " la cual difiere de la fecha escaneada: " + moment(pNueva_Fecha_Vencimiento).format('DD/MM/YYYY'));
            pDone && pDone({
              valid: false
            });
            return;
          }
          var fecha_vencimiento = pNueva_Fecha_Vencimiento ? pNueva_Fecha_Vencimiento : pDataMatrix.Fecha_Vencimiento;
          if (respuesta.Exitoso && articulo.Articulo_Vence && !moment(fecha_vencimiento).isSame(respuesta.Datos.Fecha_Vencimiento, 'day')) {
            Notification.error("El lote " + pDataMatrix.Lote + " se registro anteriormente con fecha de vencimiento " + moment(respuesta.Datos.Fecha_Vencimiento).format('DD/MM/YYYY') + " la cual difiere de la fecha escaneada: " + moment(pDataMatrix.Fecha_Vencimiento).format('DD/MM/YYYY'));
            pDone && pDone({
              valid: false
            });
            return;
          }
          if (parametros.tipo_accion === 'salida') {
            if (pDone) {
              pDone({
                valid: true
              });
            } else {
              var cantidadSalida = listaLotes.filter(function (lote) {
                return lote.Lote.toUpperCase() === pDataMatrix.Lote.toUpperCase();
              }).reduce(function (a, b) {
                return a + parseFloat(b.Cantidad);
              }, parseFloat(pDataMatrix.Cantidad));
              if (respuesta.Datos.Existencia < cantidadSalida) {
                Notification.error("La existencia del lote es menor a la que se desea restar del inventario.");
                return;
              }
              agregar_lote_lista(pDataMatrix, respuesta.Exitoso);
            }
          } else {
            if (articulo.Articulo_Vence) {
              var fecha_vence = pNueva_Fecha_Vencimiento ? moment(pNueva_Fecha_Vencimiento) : moment(pDataMatrix.Fecha_Vencimiento);
              var fecha_actual = moment();
              var dias_vencer = fecha_vence.diff(fecha_actual, 'days');
              if (parametros.tipo_accion === 'entrada_local' && articulo.Articulo_Dias_Vence > dias_vencer) {
                Swal.fire({
                  title: 'Agregar Lote',
                  text: "La cantidad de d\xEDas para vencimiento del art\xEDculo es menor a la cantidad de d\xEDas permitido. El art\xEDculo vence en " + dias_vencer + " d\xEDas. \xBFDesea ingresar el lote?",
                  showCancelButton: true,
                  confirmButtonColor: color_primario,
                  confirmButtonText: 'Ok',
                  reverseButtons: true
                }).then(function (result) {
                  if (result.isConfirmed) {
                    if (pDone) {
                      pDone({
                        valid: true
                      });
                    } else {
                      agregar_lote_lista(pDataMatrix, respuesta.Exitoso);
                    }
                  } else {
                    pDone && pDone({
                      valid: false
                    });
                  }
                });
              } else {
                if (pDone) {
                  pDone({
                    valid: true
                  });
                } else {
                  agregar_lote_lista(pDataMatrix, respuesta.Exitoso);
                }
              }
            } else {
              if (pDone) {
                pDone({
                  valid: true
                });
              } else {
                agregar_lote_lista(pDataMatrix, respuesta.Exitoso);
              }
            }
          }
        } else {
          Notification.error(respuesta.Mensaje);
          pDone && pDone({
            valid: false
          });
        }
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };
  var establecer_lote_existe = function establecer_lote_existe(pLstLotes) {
    try {
      if (!pLstLotes || pLstLotes.length === 0) {
        setListaLotes([]);
        return Promise.resolve();
      }
      var lotes_numeros = pLstLotes.map(function (lote) {
        return lote.Lote;
      });
      return Promise.resolve(Filtrar_Lotes_Existentes_Request(conexion.serviceUrl, conexion.token, parametros.empresa_id, articulo.Articulo_Id, lotes_numeros)).then(function (respuesta) {
        if (respuesta.Exitoso) {
          var lotes_verificados = pLstLotes.map(function (lote) {
            var exite_lote = respuesta.Datos.filter(function (item) {
              return item.toUpperCase() === lote.Lote.toUpperCase();
            });
            lote.Existe = exite_lote && exite_lote.length > 0;
            return lote;
          });
          setListaLotes(lotes_verificados);
        } else {
          Notification.error(respuesta.Mensaje);
          setListaLotes([]);
        }
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };
  var validar_formato_codigo_datamatrix = function validar_formato_codigo_datamatrix(pCodigoDM) {
    try {
      return Promise.resolve(Validar_Formato_Codigo_DataMatrix_Request(conexion.serviceUrl, conexion.token, parametros.empresa_id, pCodigoDM)).then(function (respuesta) {
        var _temp2 = function () {
          if (respuesta.Exitoso) {
            var _temp3 = function () {
              if (articulo.Articulo_Id !== respuesta.Datos.Articulo_Id && (!respuesta.Datos.Articulo_Codigo_Hijo || articulo.Articulo_Id !== respuesta.Datos.Articulo_Codigo_Hijo)) {
                Notification.error('El código escaneado no corresponde al artículo actual.');
              } else {
                setCodigoDM('');
                var data = {
                  Lote: respuesta.Datos.Lote,
                  Vence: respuesta.Datos.Vence,
                  Fecha_Vencimiento: obtener_fecha_sin_tiempo(new Date(respuesta.Datos.Fecha_Expiracion)),
                  Cantidad: debeSumar ? 1 : 0
                };
                setDataMatrix(data);
                setEnabledFechaVence(false);
                return Promise.resolve(Consultar_Lote_Request(conexion.serviceUrl, conexion.token, parametros.empresa_id, parametros.sucursal_id, parametros.bodega_id, articulo.Articulo_Id, respuesta.Datos.Lote)).then(function (_Consultar_Lote_Reque) {
                  respuesta = _Consultar_Lote_Reque;
                  if (respuesta.Exitoso) {
                    data = _extends({}, data, {
                      Fecha_Vencimiento: obtener_fecha_sin_tiempo(new Date(respuesta.Datos.Fecha_Vencimiento))
                    });
                    setDataMatrix(data);
                  } else {
                    setEnabledFechaVence(true);
                  }
                  if (debeSumar) {
                    validarLote(data);
                  } else {
                    inputCantidadRef.current.focus();
                  }
                });
              }
            }();
            if (_temp3 && _temp3.then) return _temp3.then(function () {});
          } else {
            Notification.error(respuesta.Mensaje);
          }
        }();
        if (_temp2 && _temp2.then) return _temp2.then(function () {});
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };
  var getPrecision = function getPrecision(pNumero) {
    if (!isFinite(pNumero)) return 0;
    var e = 1,
      p = 0;
    while (Math.round(pNumero * e) / e !== pNumero) {
      e *= 10;
      p++;
    }
    return p;
  };
  var validarLote = function validarLote(pDataMatrix) {
    try {
      if (!pDataMatrix.Lote) {
        Notification.error('Debe indicar el lote para poder continuar.');
        return Promise.resolve(false);
      }
      return Promise.resolve(validar_cantidad('', pDataMatrix.Cantidad)).then(function (respuesta) {
        if (respuesta.exitoso) {
          if (respuesta.cantidad > 0) {
            setDataMatrix(_extends({}, pDataMatrix, {
              Cantidad: respuesta.cantidad
            }));
            pDataMatrix.Cantidad = respuesta.cantidad;
          }
          consultar_lote(pDataMatrix);
        }
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };
  var validar_cantidad = function validar_cantidad(pLote, pCantidad) {
    try {
      var _temp6 = function _temp6(_result) {
        return _exit2 ? _result : {
          exitoso: true,
          cantidad: -1
        };
      };
      var _exit2 = false;
      if (!pCantidad || isNaN(pCantidad) || parseFloat(pCantidad) < 0 || parseFloat(pCantidad) === 0 && parametros.tipo_accion !== 'recepcion_interna') {
        Notification.error('La cantidad debe ser un valor numérico mayor a 0.');
        return Promise.resolve({
          exitoso: false,
          cantidad: -1
        });
      }
      var cantidad = parseFloat(pCantidad);
      if (parametros.tipo_accion !== 'tf') {
        if (listaLotes.length > 0 && debeSumar) {
          var _cantidadRegistrada = listaLotes.filter(function (lote) {
            return lote.Lote.toUpperCase() !== pLote.toUpperCase();
          }).reduce(function (a, b) {
            return a + parseFloat(b.Cantidad);
          }, 0);
          if (cantidad + _cantidadRegistrada > parametros.cantidad_requerida) {
            Notification.error("La cantidad ingresada es mayor a la cantidad requerida.");
            return Promise.resolve({
              exitoso: false,
              cantidad: -1
            });
          }
        } else {
          if (cantidad > parametros.cantidad_requerida) {
            Notification.error("La cantidad ingresada es mayor a la cantidad requerida.");
            return Promise.resolve({
              exitoso: false,
              cantidad: -1
            });
          }
        }
      }
      var cantidad_decimales = getPrecision(cantidad);
      if (cantidad_decimales > 0) {
        if (articulo.Decimales) {
          if (cantidad_decimales > parametros.cantidad_decimales) {
            Notification.warning("El art\xEDculo solo permite el uso de " + parametros.cantidad_decimales + " decimales.");
            var _potencia = Math.pow(10, parametros.cantidad_decimales);
            return Promise.resolve({
              exitoso: true,
              cantidad: Math.round((cantidad + Number.EPSILON) * _potencia) / _potencia
            });
          }
        } else {
          if (cantidad_decimales > 0) {
            Notification.warning("El artículo no admite el uso de decimales, se agregará solo la cantidad entera.");
            var cantidad_sin_decimales = Math.round(cantidad);
            if (cantidad_sin_decimales > 0) {
              return Promise.resolve({
                exitoso: true,
                cantidad: Math.round(cantidad)
              });
            } else {
              Notification.error('La cantidad debe ser un valor numérico mayor a 0.');
              return Promise.resolve({
                exitoso: false,
                cantidad: -1
              });
            }
          }
        }
      }
      var _temp7 = function () {
        if (pLote && parametros.tipo_accion === 'salida') {
          return Promise.resolve(Consultar_Lote_Request(conexion.serviceUrl, conexion.token, parametros.empresa_id, parametros.sucursal_id, parametros.bodega_id, articulo.Articulo_Id, pLote)).then(function (respuesta) {
            if (respuesta.Exitoso) {
              if (respuesta.Datos.Existencia < parseFloat(pCantidad)) {
                Notification.error("La existencia del lote es menor a la que se desea restar del inventario.");
                var _exitoso$cantidad2 = {
                  exitoso: false,
                  cantidad: -1
                };
                _exit2 = true;
                return _exitoso$cantidad2;
              }
            }
          });
        }
      }();
      return Promise.resolve(_temp7 && _temp7.then ? _temp7.then(_temp6) : _temp6(_temp7));
    } catch (e) {
      return Promise.reject(e);
    }
  };
  var validar_devolucion = function validar_devolucion(pLote, pDevolucion) {
    if (isNaN(pDevolucion) || parseFloat(pDevolucion) < 0) {
      Notification.error('La devolución debe ser un valor numérico mayor o igual a 0.');
      return {
        exitoso: false,
        devolucion: -1
      };
    }
    var devolucion = parseFloat(pDevolucion);
    if (devolucion > pLote.Cantidad) {
      Notification.error("La cantidad a devolver es mayor a la cantidad existente.");
      return {
        exitoso: false,
        devolucion: -1
      };
    }
    var cantidad_decimales = getPrecision(devolucion);
    if (cantidad_decimales > 0) {
      if (articulo.Decimales) {
        if (cantidad_decimales > parametros.cantidad_decimales) {
          Notification.warning("El art\xEDculo solo permite el uso de " + parametros.cantidad_decimales + " decimales.");
          var _potencia2 = Math.pow(10, parametros.cantidad_decimales);
          return {
            exitoso: true,
            devolucion: Math.round((devolucion + Number.EPSILON) * _potencia2) / _potencia2
          };
        }
      } else {
        if (cantidad_decimales > 0) {
          Notification.warning("El artículo no admite el uso de decimales, se agregará solo la cantidad entera.");
          return {
            exitoso: true,
            devolucion: Math.round(devolucion)
          };
        }
      }
    }
    return {
      exitoso: true,
      devolucion: -1
    };
  };
  var agregar_lote_lista = function agregar_lote_lista(pDataMatrix, pLoteExiste) {
    var lotes = [].concat(listaLotes);
    var index = lotes.findIndex(function (lote) {
      return lote.Lote.toUpperCase() === pDataMatrix.Lote.toUpperCase();
    });
    if (index >= 0) {
      lotes[index].Cantidad = debeSumar ? parseFloat(lotes[index].Cantidad) + parseFloat(pDataMatrix.Cantidad) : parseFloat(pDataMatrix.Cantidad);
      if (parametros.tipo_accion === 'recepcion_interna' && lstEnviados) {
        lotes[index].Diferencia = (parseFloat(lotes[index].Enviado) - lotes[index].Cantidad).toFixed(parametros.cantidad_decimales || 0);
      }
    } else {
      var newLote = _extends({}, pDataMatrix);
      newLote.Articulo_Id = articulo.Articulo_Id;
      newLote.Codigo_GTIN = articulo.Codigo_Principal;
      newLote.Vence = articulo.Articulo_Vence;
      newLote.Codigo_Padre = articulo.Codigo_Padre;
      newLote.Existe = pLoteExiste;
      newLote.Devolucion = 0;
      if (parametros.tipo_accion === 'recepcion_interna' && lstEnviados) {
        var enviado_filtrado = lstEnviados.length > 0 ? lstEnviados.filter(function (item) {
          return item.Articulo_Id === newLote.Articulo_Id && item.Lote.toUpperCase() === newLote.Lote.toUpperCase();
        }) : [];
        newLote.Enviado = enviado_filtrado && enviado_filtrado.length > 0 ? enviado_filtrado[0].Enviado : 0;
        newLote.Diferencia = (parseFloat(newLote.Enviado) - parseFloat(newLote.Cantidad)).toFixed(parametros.cantidad_decimales || 0);
      }
      lotes.push(newLote);
    }
    setListaLotes(lotes);
    setEnabledFechaVence(true);
    setDataMatrix({
      Lote: '',
      Vence: false,
      Fecha_Vencimiento: obtener_fecha_sin_tiempo(new Date()),
      Cantidad: 0
    });
    setHayCambios(true);
    if (debeSumar) {
      inputCodigoDMRef.current.focus();
    } else {
      inputCantidadRef.current.focus();
    }
  };
  var reset_states = function reset_states() {
    setCodigoDM('');
    setDataMatrix({
      Lote: '',
      Vence: false,
      Fecha_Vencimiento: obtener_fecha_sin_tiempo(new Date()),
      Cantidad: 0
    });
    setSelected([]);
    setDebeSumar(true);
    setEnabledFechaVence(true);
    setHayCambios(false);
  };
  var accion_evento_lote = function accion_evento_lote() {
    if (dataMatrix.Lote) {
      var lote = listaLotes.find(function (lote) {
        return lote.Lote.toUpperCase() === dataMatrix.Lote.toUpperCase();
      });
      if (lote) {
        setEnabledFechaVence(false);
        setDataMatrix(_extends({}, dataMatrix, {
          Fecha_Vencimiento: obtener_fecha_sin_tiempo(new Date(lote.Fecha_Vencimiento))
        }));
        inputCantidadRef.current.focus();
      } else {
        setEnabledFechaVence(true);
        obtener_fecha_vencimiento_lote(dataMatrix.Lote);
      }
    }
  };
  var onChangedCodigoDM = function onChangedCodigoDM(event) {
    var value = event.target.value;
    setCodigoDM(value.trim());
  };
  var onChangedAction = function onChangedAction(event) {
    var value = event.target.value;
    setDebeSumar(value === '1');
  };
  var onChangedDataMatrix = function onChangedDataMatrix(event) {
    var _event$target = event.target,
      value = _event$target.value,
      name = _event$target.name;
    var newValue = value;
    if (name === 'Lote') {
      newValue = value.replace(/[^a-zA-Z0-9]/gi, '');
    }
    if (name === 'Cantidad' && value.length > 22) {
      return false;
    }
    if (name === 'Fecha_Vencimiento') {
      var dueDate = moment(value);
      var isValid = dueDate.isValid();
      if (!isValid) {
        return false;
      }
    }
    setDataMatrix(function (prevState) {
      var _extends2;
      return _extends({}, prevState, (_extends2 = {}, _extends2[name] = newValue, _extends2));
    });
  };
  var onCloseModal = function onCloseModal(saveAction) {
    if (saveAction === void 0) {
      saveAction = false;
    }
    if (!saveAction && hayCambios) {
      Swal.fire({
        title: 'Salir de Pantalla',
        text: '¿Desea salir sin guardar los cambios?',
        showCancelButton: true,
        confirmButtonColor: color_primario,
        confirmButtonText: 'Ok',
        reverseButtons: true
      }).then(function (result) {
        if (result.isConfirmed) {
          onClose(lstLotes, saveAction);
          setModalShow(false);
          setCodigoDM('');
        }
      });
    } else {
      setModalShow(false);
      setCodigoDM('');
      onClose(listaLotes, saveAction && hayCambios);
    }
  };
  var onClickBtnAgregar = function onClickBtnAgregar() {
    if (!parametros.modo_consulta) {
      validarLote(dataMatrix);
    }
  };
  var onClickBtnEliminar = function onClickBtnEliminar() {
    if (!parametros.modo_consulta) {
      if (selected && selected.length > 0) {
        var lotes = [].concat(listaLotes);
        selected.forEach(function (lote_numero) {
          var index = lotes.findIndex(function (lote) {
            return lote.Lote.toUpperCase() === lote_numero.toUpperCase();
          });
          if (index >= 0) {
            lotes.splice(index, 1);
          }
        });
        setListaLotes(lotes);
        setHayCambios(true);
        setSelected([]);
      } else {
        Notification.error("Debe seleccionar al menos un registro a eliminar.");
      }
    }
  };
  var onKeyPressModal = function onKeyPressModal(event) {
    event.stopPropagation();
  };
  var onKeyDownCodigoDM = function onKeyDownCodigoDM(event) {
    event.stopPropagation();
    if (event.key === 'Enter') {
      if (!codigoDM || !codigoDM.trim()) {
        Notification.error("Antes de continuar debe indicar el código datamatrix.");
        return false;
      }
      if (codigoDM.length < 40) {
        Notification.error("El código indicado no cumple con las dimensiones mínimas de un código datamatrix.");
        return false;
      }
      validar_formato_codigo_datamatrix(codigoDM);
    }
  };
  var onKeyDownLote = function onKeyDownLote(event) {
    if (event.keyCode === 9 || event.keyCode === 13) {
      accion_evento_lote();
    }
  };
  var onBlurLote = function onBlurLote(event) {
    accion_evento_lote();
  };
  var onKeyDownCantidad = function onKeyDownCantidad(event) {
    event.stopPropagation();
    if (event.key === 'Enter') {
      validarLote(dataMatrix);
    }
  };
  var onPasteCantidad = function onPasteCantidad(event) {
    var pastedValue = event.clipboardData.getData("text");
    if (pastedValue.length > 22) {
      event.preventDefault();
      setDataMatrix(function (prevState) {
        return _extends({}, prevState, {
          Cantidad: pastedValue.substring(0, 22)
        });
      });
    }
  };
  var onFocusCantidad = function onFocusCantidad(event) {
    event.target.select();
  };
  var onClickBtnGuardar = function onClickBtnGuardar() {
    switch (parametros.tipo_accion) {
      case 'entrada_local':
        var _cantidadRegistrada2 = listaLotes.reduce(function (a, b) {
          return a + (parseFloat(b.Cantidad) - parseFloat(b.Devolucion || '0'));
        }, 0);
        var _cantidadPendiente = 0;
        if (parametros.cantidad_solicitada > parametros.cantidad_requerida) {
          _cantidadPendiente = Math.round((parametros.cantidad_requerida - _cantidadRegistrada2 + Number.EPSILON) * potencia) / potencia;
        } else {
          _cantidadPendiente = Math.round((parametros.cantidad_solicitada - _cantidadRegistrada2 + Number.EPSILON) * potencia) / potencia;
        }
        if (_cantidadPendiente !== 0) {
          Notification.error("La cantidad pendiente es diferente de 0.");
          return false;
        }
        onCloseModal(true);
        break;
      default:
        onCloseModal(true);
    }
  };
  var onSelect = function onSelect(row, isSelect) {
    if (isSelect) {
      setSelected([].concat(selected, [row.Lote]));
    } else {
      setSelected([].concat(selected.filter(function (x) {
        return x.toUpperCase() !== row.Lote.toUpperCase();
      })));
    }
  };
  var onSelectAll = function onSelectAll(isSelect, rows) {
    var ids = rows.map(function (x) {
      return x.Lote;
    });
    if (isSelect) {
      setSelected(ids);
    } else {
      setSelected([]);
    }
  };
  var columns = [{
    dataField: 'Lote',
    text: 'Lote',
    headerAlign: 'left',
    align: 'left',
    editable: false,
    headerClasses: !listaLotes || listaLotes.length === 0 ? styles['header-min-width'] : ''
  }, {
    dataField: 'Vence',
    text: 'Vence',
    headerAlign: 'center',
    align: 'center',
    editable: false,
    headerStyle: {
      width: '60px'
    },
    style: {
      width: '60px'
    },
    formatter: function formatter(cell, row) {
      return row.Vence ? 'Sí' : 'No';
    }
  }, {
    dataField: 'Fecha_Vencimiento',
    text: 'Fecha Vencimiento',
    headerAlign: 'center',
    align: 'center',
    editable: function editable(content, row, rowIndex, columnIndex) {
      return row.Vence && !parametros.modo_consulta && !row.Existe;
    },
    headerStyle: {
      width: parametros.tipo_accion === 'recepcion_interna' ? '140px' : '158px'
    },
    style: {
      width: parametros.tipo_accion === 'recepcion_interna' ? '140px' : '158px'
    },
    formatter: function formatter(cell, row) {
      if (row.Vence) {
        var fecha_vence = moment(row.Fecha_Vencimiento);
        var fecha_actual = moment();
        var dias_vencer = fecha_vence.diff(fecha_actual, 'days');
        if (dias_vencer < 0) {
          var date = moment(cell).format("DD/MM/YYYY");
          return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TiWarning, {
            color: "#EB1414",
            title: "Vencido"
          }), " ", date);
        } else {
          if (dias_vencer < articulo.Articulo_Dias_Vence) {
            var _date = moment(cell).format("DD/MM/YYYY");
            return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TiWarning, {
              color: "#FFC107",
              title: "Por Vencer"
            }), " ", _date);
          }
        }
      } else {
        return 'N/A';
      }
      return moment(cell).format("DD/MM/YYYY");
    },
    editor: {
      type: Type.DATE
    },
    validator: function validator(newValue, row, column, done) {
      if (row.Vence) {
        consultar_lote(row, function (valid) {
          if (valid.valid) return done();else return done(valid);
        }, newValue);
        return {
          async: true
        };
      } else {
        return {
          valid: false
        };
      }
    }
  }, {
    dataField: 'Cantidad',
    text: 'Cantidad',
    headerAlign: 'right',
    align: 'right',
    editable: !parametros.modo_consulta,
    headerStyle: {
      width: parametros.tipo_accion === 'recepcion_interna' ? '100px' : '135px'
    },
    style: {
      width: parametros.tipo_accion === 'recepcion_interna' ? '100px' : '135px'
    },
    formatter: function formatter(cell, row) {
      return parseFloat(row.Cantidad);
    },
    validator: function (newValue, row, column, done) {
      try {
        return Promise.resolve(validar_cantidad(row.Lote, newValue)).then(function (respuesta) {
          return !respuesta.exitoso || respuesta.cantidad > 0 ? done({
            valid: false
          }) : done();
        });
      } catch (e) {
        return Promise.reject(e);
      }
    }
  }];
  if (parametros.tipo_accion === 'entrada_local') {
    columns.push({
      dataField: 'Devolucion',
      text: 'Devolución',
      headerAlign: 'right',
      align: 'right',
      editable: !parametros.modo_consulta,
      headerStyle: {
        width: '135px'
      },
      style: {
        width: '135px'
      },
      formatter: function formatter(cell, row) {
        return parseFloat(row.Devolucion || '0');
      },
      validator: function validator(newValue, row, column) {
        var respuesta = validar_devolucion(row, newValue);
        if (!respuesta.exitoso || respuesta.devolucion > 0) {
          return {
            valid: false
          };
        }
        return true;
      }
    });
  }
  if (parametros.tipo_accion === 'recepcion_interna' && lstEnviados) {
    columns.push({
      dataField: 'Enviado',
      text: 'Enviado',
      headerAlign: 'right',
      align: 'right',
      editable: false,
      headerStyle: {
        width: '86px'
      },
      style: {
        width: '86px'
      }
    });
    columns.push({
      dataField: 'Diferencia',
      text: 'Diferencia',
      headerAlign: 'right',
      align: 'right',
      editable: false,
      headerStyle: {
        width: '94px'
      },
      style: {
        width: '94px'
      }
    });
  }
  var selectRow = {
    mode: 'checkbox',
    clickToSelect: false,
    selected: selected,
    nonSelectable: nonSelectable,
    onSelect: onSelect,
    onSelectAll: onSelectAll
  };
  var cellEdit = {
    mode: 'dbclick',
    blurToSave: true,
    beforeSaveCell: function beforeSaveCell(oldValue, newValue, row, column) {
      if (newValue !== oldValue) {
        var lotes = [].concat(listaLotes);
        var index = lotes.findIndex(function (lote) {
          return lote.Lote.toUpperCase() === row.Lote.toUpperCase();
        });
        if (index >= 0) {
          lotes[index][column.dataField] = column.dataField === 'Cantidad' ? parseFloat(newValue) : obtener_fecha_sin_tiempo(new Date(newValue));
          if (parametros.tipo_accion === 'recepcion_interna' && lstEnviados && column.dataField === 'Cantidad') {
            lotes[index].Diferencia = (parseFloat(lotes[index].Enviado) - parseFloat(lotes[index].Cantidad)).toFixed(parametros.cantidad_decimales || 0);
          }
        }
        setListaLotes(lotes);
        setHayCambios(true);
      }
    }
  };
  var cantidadRegistrada = listaLotes.reduce(function (a, b) {
    return a + parseFloat(b.Cantidad);
  }, 0);
  var cantidadDevuelta = listaLotes.reduce(function (a, b) {
    return a + parseFloat(b.Devolucion || '0');
  }, 0);
  var potencia = Math.pow(10, parametros.cantidad_decimales);
  var cantidadPendiente = 0;
  if (parametros.cantidad_solicitada && parametros.cantidad_solicitada > 0) {
    if (parametros.cantidad_solicitada > parametros.cantidad_requerida) {
      cantidadPendiente = Math.round((parametros.cantidad_requerida - cantidadRegistrada + cantidadDevuelta + Number.EPSILON) * potencia) / potencia;
    } else {
      cantidadPendiente = Math.round((parametros.cantidad_solicitada - cantidadRegistrada + cantidadDevuelta + Number.EPSILON) * potencia) / potencia;
    }
  } else {
    cantidadPendiente = Math.round((parametros.cantidad_requerida - cantidadRegistrada + Number.EPSILON) * potencia) / potencia;
  }
  var radioStyle = {
    backgroundColor: color_primario,
    borderColor: color_primario
  };
  return /*#__PURE__*/React.createElement("div", {
    onKeyPress: onKeyPressModal,
    onKeyDown: onKeyPressModal,
    onKeyUp: onKeyPressModal
  }, /*#__PURE__*/React.createElement(Modal, {
    show: modalShow,
    onHide: onCloseModal,
    backdrop: "static",
    size: "lg"
  }, /*#__PURE__*/React.createElement(Modal.Header, {
    closeButton: true
  }, /*#__PURE__*/React.createElement(Modal.Title, null, "Registro de Lotes")), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-12"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Art\xEDculo"), /*#__PURE__*/React.createElement(Form.Control, {
    type: "text",
    className: styles['input-control'],
    value: articulo.Articulo_Id + " - " + articulo.Nombre,
    readOnly: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: "row mt-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-12 col-md-auto"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Vence"), /*#__PURE__*/React.createElement(Form.Control, {
    type: "text",
    className: styles['input-control'],
    value: articulo.Articulo_Vence ? 'Sí' : 'No',
    readOnly: true
  })), articulo.Articulo_Vence ? /*#__PURE__*/React.createElement("div", {
    className: "col-12 col-md-auto"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "L\xEDmite Vencimiento"), /*#__PURE__*/React.createElement(InputGroup, null, /*#__PURE__*/React.createElement(Form.Control, {
    type: "text",
    className: styles['input-control'],
    value: articulo.Articulo_Dias_Vence,
    readOnly: true
  }), /*#__PURE__*/React.createElement(InputGroup.Text, {
    id: "days-addon"
  }, "D\xEDas"))) : null), /*#__PURE__*/React.createElement("div", {
    className: "row mt-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-12 col-sm"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Datamatrix"), /*#__PURE__*/React.createElement(Form.Control, {
    type: "text",
    className: styles['input-control'],
    value: codigoDM.trim(),
    maxLength: "100",
    ref: inputCodigoDMRef,
    disabled: parametros.modo_consulta,
    onChange: onChangedCodigoDM,
    onKeyDown: onKeyDownCodigoDM
  })), /*#__PURE__*/React.createElement("div", {
    className: "col-12 col-sm-auto my-auto"
  }, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Form.Check, {
    inline: true,
    type: "radio",
    name: "rbtnAccion"
  }, /*#__PURE__*/React.createElement(Form.Check.Input, {
    id: "chkSumar",
    type: "radio",
    style: debeSumar ? radioStyle : {},
    value: "1",
    disabled: parametros.modo_consulta,
    onChange: onChangedAction,
    checked: debeSumar != null ? debeSumar : false
  }), /*#__PURE__*/React.createElement(Form.Check.Label, {
    htmlFor: "chkSumar"
  }, "Sumar")), /*#__PURE__*/React.createElement(Form.Check, {
    inline: true,
    type: "radio",
    name: "rbtnAccion"
  }, /*#__PURE__*/React.createElement(Form.Check.Input, {
    id: "chkDigitar",
    type: "radio",
    style: debeSumar ? {} : radioStyle,
    value: "2",
    disabled: parametros.modo_consulta,
    onChange: onChangedAction,
    checked: debeSumar ? false : true
  }), /*#__PURE__*/React.createElement(Form.Check.Label, {
    htmlFor: "chkDigitar"
  }, "Digitar")))), /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-12 col-sm-6 mt-3 " + (articulo.Articulo_Vence ? 'col-lg-5' : 'col-lg-7')
  }, /*#__PURE__*/React.createElement(Form.Label, {
    htmlFor: "txtLote"
  }, "Lote"), /*#__PURE__*/React.createElement(Form.Control, {
    id: "txtLote",
    name: "Lote",
    type: "text",
    className: styles['input-control'],
    value: dataMatrix.Lote,
    maxLength: "26",
    disabled: parametros.modo_consulta,
    onChange: onChangedDataMatrix,
    onKeyDown: onKeyDownLote,
    onBlur: onBlurLote
  })), articulo.Articulo_Vence ? /*#__PURE__*/React.createElement("div", {
    className: "col-12 col-sm-6 col-md-4 col-lg-3 mt-3"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Fecha Vencimiento"), /*#__PURE__*/React.createElement(DatePickerComponent, {
    name: "Fecha_Vencimiento",
    classNameInput: styles['input-control'],
    wrapperClassName: styles['block'],
    value: dataMatrix.Fecha_Vencimiento,
    disabled: parametros.modo_consulta || !enabledFechaVence,
    onChange: onChangedDataMatrix
  })) : null, /*#__PURE__*/React.createElement("div", {
    className: "col-6 col-lg mt-3"
  }, /*#__PURE__*/React.createElement(Form.Label, {
    htmlFor: "txtCantidad"
  }, "Cantidad"), /*#__PURE__*/React.createElement(Form.Control, {
    id: "txtCantidad",
    name: "Cantidad",
    type: "number",
    className: styles['input-control'],
    value: dataMatrix.Cantidad,
    min: "1",
    max: parametros.cantidad_requerida,
    disabled: parametros.modo_consulta,
    step: articulo.Decimales ? "0." + '1'.padStart(parametros.cantidad_decimales, '0') : '1',
    ref: inputCantidadRef,
    onFocus: onFocusCantidad,
    onChange: onChangedDataMatrix,
    onKeyDown: onKeyDownCantidad,
    onPaste: onPasteCantidad
  })), /*#__PURE__*/React.createElement("div", {
    className: "col-auto mt-3",
    style: {
      height: '70px'
    }
  }, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-auto"
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      cursor: 'pointer',
      lineHeight: '50px'
    },
    onClick: onClickBtnAgregar
  }, /*#__PURE__*/React.createElement(FaPlusCircle, {
    color: color_primario,
    size: '1.5em'
  }))), /*#__PURE__*/React.createElement("div", {
    className: "col-auto"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ml-2",
    style: {
      cursor: 'pointer',
      lineHeight: '50px'
    },
    onClick: onClickBtnEliminar
  }, /*#__PURE__*/React.createElement(FaMinusCircle, {
    color: color_primario,
    size: '1.5em'
  })))))), /*#__PURE__*/React.createElement("div", {
    className: "row mt-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-12",
    style: {
      maxHeight: '150px',
      overflowY: 'auto',
      overflowX: 'auto'
    }
  }, /*#__PURE__*/React.createElement(BootstrapTable, {
    keyField: "Lote",
    data: listaLotes,
    columns: columns,
    selectRow: selectRow,
    cellEdit: cellEditFactory(cellEdit)
  }))), /*#__PURE__*/React.createElement("div", {
    className: "row mt-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-auto"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Escaneada: ", /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 'bold'
    }
  }, cantidadRegistrada.toFixed(parametros.cantidad_decimales || 0)))), parametros.tipo_accion !== 'tf' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "col-auto"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Pendiente: ", /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 'bold'
    }
  }, cantidadPendiente))), /*#__PURE__*/React.createElement("div", {
    className: "col-auto"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Requerida: ", /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 'bold'
    }
  }, parametros.cantidad_requerida)))) : null, parametros.cantidad_solicitada && parametros.cantidad_solicitada > 0 ? /*#__PURE__*/React.createElement("div", {
    className: "col-auto"
  }, /*#__PURE__*/React.createElement(Form.Label, null, "Solicitado: ", /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 'bold'
    }
  }, parametros.cantidad_solicitada))) : null, /*#__PURE__*/React.createElement("div", {
    className: "col-auto ms-auto"
  }, /*#__PURE__*/React.createElement(Button, {
    style: {
      backgroundColor: color_primario
    },
    onClick: onClickBtnGuardar,
    disabled: parametros.modo_consulta
  }, "Guardar"))))));
};
LotesComponent.propTypes = {
  show: PropTypes.bool,
  conexion: PropTypes.shape({
    token: PropTypes.string.isRequired,
    serviceUrl: PropTypes.string.isRequired
  }).isRequired,
  parametros: PropTypes.shape({
    empresa_id: PropTypes.number.isRequired,
    sucursal_id: PropTypes.number.isRequired,
    bodega_id: PropTypes.number.isRequired,
    modulo_id: PropTypes.number.isRequired,
    menu_id: PropTypes.number.isRequired,
    tipo_accion: PropTypes.oneOf(['entrada', 'salida', 'tf', 'entrada_local', 'recepcion_interna']),
    cantidad_requerida: PropTypes.number.isRequired,
    cantidad_solicitada: PropTypes.number.isRequired,
    cantidad_decimales: PropTypes.number.isRequired
  }).isRequired,
  style: PropTypes.shape({
    color_primario: PropTypes.string
  }),
  articulo: PropTypes.shape({
    Articulo_Id: PropTypes.string,
    Codigo_Principal: PropTypes.string,
    Nombre: PropTypes.string,
    Articulo_Dias_Vence: PropTypes.number,
    Articulo_Vence: PropTypes.bool,
    Decimales: PropTypes.bool
  }).isRequired,
  lstLotes: PropTypes.arrayOf(PropTypes.shape({
    Lote: PropTypes.string,
    Vence: PropTypes.bool,
    Fecha_Vencimiento: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    Cantidad: PropTypes.number,
    Devolucion: PropTypes.number
  })),
  lstEnviados: PropTypes.arrayOf(PropTypes.shape({
    Lote: PropTypes.string,
    Articulo_Id: PropTypes.string,
    Enviado: PropTypes.number
  })),
  onClose: PropTypes.func
};

export default LotesComponent;
//# sourceMappingURL=index.modern.js.map
